import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Desktop WebKit Safari (not Chrome/Firefox/Edge): `body { position: fixed }` + modal `<dialog>`
 * + `backdrop-filter` on `::backdrop` can freeze the page and blank text (e.g. over video) in recent Safari.
 * Use document overflow scroll lock and CSS disables backdrop-filter while `data-dialog-scroll-lock` is set.
 * @returns {boolean}
 */
function prefersDocumentOverflowScrollLock() {
  if (typeof navigator === 'undefined' || typeof document === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/(iPhone|iPad|iPod)/i.test(ua)) return false;
  if (!/Safari/i.test(ua)) return false;
  if (/Chrome|CriOS|Chromium|Edg|EdgiOS|OPR|Firefox|FxiOS|Android/i.test(ua)) return false;
  return true;
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {null | 'body-fixed' | 'doc-overflow'} */
  #scrollLockMode = null;

  /** Prevents concurrent `closeDialog` runs when both `cancel` and `keydown` fire for Escape. */
  #closeDialogPromise = /** @type {Promise<void> | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Document scroll position for lock restore. Prefer `scrollingElement` for Safari.
   * @returns {number}
   */
  #readDocumentScrollTop() {
    const root = document.scrollingElement;
    if (root) return root.scrollTop;
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  #releaseDocumentScrollLock() {
    if (!this.#scrollLockMode) return;

    if (this.#scrollLockMode === 'doc-overflow') {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.documentElement.removeAttribute('data-dialog-scroll-lock');
    } else {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
    }

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.headerStickyScrollSuspend, {
        detail: {
          delta: -1,
          syncDocumentScrollTop: this.#previousScrollY,
        },
      }),
    );

    this.#scrollLockMode = null;
  }

  /**
   * Unlock overflow before restoring so iOS/Chromium actually honor the scroll position.
   */
  #restoreDocumentScroll() {
    const y = this.#previousScrollY;

    this.#releaseDocumentScrollLock();

    const root = document.scrollingElement ?? document.documentElement;
    root.scrollTop = y;
    window.scrollTo({ top: y, behavior: 'instant' });
  }

  /**
   * WebKit may close a modal `<dialog>` from Escape without running our full `closeDialog`
   * (e.g. `cancel` not paired with our teardown). Always clear body scroll lock on `close`.
   */
  #handleDialogClose = () => {
    const { dialog } = this.refs;
    dialog.removeEventListener('close', this.#handleDialogClose);

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('cancel', this.#handleDialogCancel);

    if (!this.#scrollLockMode) return;

    this.#restoreDocumentScroll();
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Keep Escape on the spec `cancel` path so we never rely on `keydown` default alone (Safari).
   * @param {Event} event
   */
  #handleDialogCancel = (event) => {
    event.preventDefault();
    void this.closeDialog();
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Capture scroll in the same frame as the lock so momentum scroll (e.g. after ATC) does not desync WebKit.
    requestAnimationFrame(() => {
      if (dialog.open) return;

      const scrollTop = this.#readDocumentScrollTop();
      this.#previousScrollY = scrollTop;

      if (prefersDocumentOverflowScrollLock()) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.documentElement.setAttribute('data-dialog-scroll-lock', 'overflow');
        this.#scrollLockMode = 'doc-overflow';
      } else {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollTop}px`;
        this.#scrollLockMode = 'body-fixed';
      }

      dialog.showModal();
      dialog.addEventListener('cancel', this.#handleDialogCancel);
      dialog.addEventListener('close', this.#handleDialogClose);

      this.dispatchEvent(new DialogOpenEvent());

      document.dispatchEvent(
        new CustomEvent(ThemeEvents.headerStickyScrollSuspend, {
          detail: {
            delta: 1,
            syncDocumentScrollTop: 0,
          },
        }),
      );

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    if (this.#closeDialogPromise) return this.#closeDialogPromise;

    this.#closeDialogPromise = this.#closeDialogImpl();
    try {
      await this.#closeDialogPromise;
    } finally {
      this.#closeDialogPromise = null;
    }
  };

  #closeDialogImpl = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) {
      this.#releaseDocumentScrollLock();
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('cancel', this.#handleDialogCancel);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Restore scroll after unlock: Chromium often ignores window.scrollTo while body is position:fixed.
    this.#restoreDocumentScroll();

    dialog.removeEventListener('close', this.#handleDialogClose);
    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    // `cancel` on `<dialog>` is authoritative for Escape; this covers engines that omit it.
    void this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
